import React from "react";
import { orderStatusConfig } from "../../types/types";
import OrderStatusCell from "./OrderStatusCell";
import { Select, MenuItem, SelectChangeEvent } from "@mui/material";

interface StatusDropdownProps {
  id: string;
  isDisabled: boolean;
  invoiceDraftStatus: string;
  onStatusChange?: (rowId: string, newStatus: string) => void;
  statusConfig?: orderStatusConfig[];
}

const StatusDropdown: React.FC<StatusDropdownProps> = ({ id, isDisabled, invoiceDraftStatus, onStatusChange, statusConfig }) => {
  
  // If invoiceStatus is "invoiced", show dropdown with preselected value from invoiceDraftStatus
  const statusOptions = (statusConfig || []).filter(status => 
    ["DELIVERED", "CANCELLED", "PARTIAL DELIVERED"].includes(status.id)
  );
  
  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    if (isDisabled) return; // Prevent status change if disabled
    
    const newStatus = event.target.value;
    // Update the row data with the new status
    invoiceDraftStatus = newStatus;
    // Call the parent callback if provided
    if (onStatusChange) {
      onStatusChange(id, newStatus);
    }
  };

  const getSelectedStyle = (status: string) => {
    const statusData = statusConfig?.find(config => 
      config.id?.toLowerCase() === status?.toLowerCase()
    );
    
    if (statusData) {
      return { 
        color: statusData.dmsLabelColor || "#227c9d", 
        backgroundColor: statusData.dmsColor || "#f0fbff" 
      };
    }
    
    // Fallback to default colors if no config found
    return { color: "#227c9d", backgroundColor: "#f0fbff" };
  };

  const currentValue = invoiceDraftStatus || "DELIVERED";

  return (
    <Select
      value={currentValue}
      onChange={handleStatusChange}
      onClick={(e) => {
        if (isDisabled) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        e.stopPropagation();
      }}
      disabled={isDisabled}
      IconComponent={isDisabled ? undefined : undefined} // Hide chevron when disabled
      sx={{
        border: "1px solid",
        fontSize: "12px",
        fontWeight: "500",
        borderRadius: "6px",
        minWidth: "5.5rem",
        height: "2rem",
        width: "7.25rem",
        "& .MuiOutlinedInput-notchedOutline": {
          border: "none",
        },
        "& .MuiSelect-select": {
          padding: "4px 8px",
        },
        "& .MuiSelect-icon": {
          color: getSelectedStyle(currentValue).color,
          transition: "color 0.3s ease",
          display: isDisabled ? "none" : "block", // Hide chevron when disabled
        },
        "&.Mui-disabled": {
          backgroundColor: getSelectedStyle(currentValue).backgroundColor,
          color: getSelectedStyle(currentValue).color,
          "& .MuiSelect-select": {
            color: getSelectedStyle(currentValue).color,
          },
        },
        transition: "color 0.3s ease, background-color 0.3s ease",
        ...getSelectedStyle(currentValue),
      }}
    >
      {statusOptions.map((option) => (
        <MenuItem 
          key={option.id} 
          value={option.id}
          disabled={isDisabled}
          sx={{ 
            border: "none", 
            color: "#909090",
            fontSize: "12px",
            padding: "4px 8px"
          }}
        >
          {option.dmsLabel || option.id}
        </MenuItem>
      ))}
    </Select>
  );
};

export default StatusDropdown;
